// R123 (Jesse: "bolster the info we can gather about a WO, its progress, its
// scheduling date, due date"): the ONE set of work-order dispatch fields the
// Issues board uses wherever it books a WO: the route modal's work-order
// branch (single + bulk) and the WoQuickView Who editor's roster. Person,
// Vendor (by id, never folded into assigned_to), Day (with Today / Tomorrow
// chips in Pacific time) and Due (a date, or "at next checkout").
//
// CONTRACT (R123 §8.5): loadDispatchRosters / woDispatchFieldsHtml /
// bindWoDispatchFields / readWoDispatchFields.
// Look: the Calendar inspector's form idiom (small uppercase labels, soft
// bordered inputs, chips for the quick days). Two columns on wide screens,
// one column at ≤ 640px; every control ≥ 36px and every input 16px on phones
// (no iOS focus zoom); tokens only.
import { Formatters } from '../../shared/lib/formatters';
import { bizDay } from '../../../../lib/issueStage.mjs';
import { addDaysStr } from '../../../../lib/woCalendar.mjs';

const API = '/api/assetops';
const esc = (s: any): string => Formatters.escapeHtml(s == null ? '' : String(s));
const DAY = /^\d{4}-\d{2}-\d{2}$/;

export interface RosterPerson { name: string; kind: string }
export interface RosterVendor { id: string; name: string }
export interface DispatchRosters { people: RosterPerson[]; vendors: RosterVendor[] }
/** What the fields read back. Only filled fields are present; a vendor never lands in assigned_to. */
export interface WoDispatchValue {
  assigned_to?: string;
  vendor_id?: string;
  scheduled_date?: string;
  due_date?: string;
  due_source?: 'checkout' | null;
}

const ROLE_LABEL: Record<string, string> = { admin: 'Admin', director: 'Director', manager: 'Manager', remote_ops: 'Remote ops', maintenance: 'Maintenance', inspector: 'Inspector', cleaner: 'Cleaner', owner: 'Owner' };   // the route modal's labels
let rosterCache: Promise<DispatchRosters> | null = null;

/** Everyone a WO can go to: team ∪ cleaners ∪ inspectors (people) and vendor companies (by id). Cached per page load. */
export function loadDispatchRosters(): Promise<DispatchRosters> {
  if (rosterCache) return rosterCache;
  let partial = false;
  const get = (url: string) => fetch(url).then(async r => {
    const j = r.ok ? await r.json().catch(() => null) : null;
    if (!j || j.roster_unavailable) partial = true;   // R120: a failed roster read says so, never "nobody"
    return j;
  }).catch(() => { partial = true; return null; });
  const p = Promise.all([get(`${API}/vendors`), get(`${API}/cleaners`), get(`${API}/inspections/managers`), get('/api/tasks/assignees')]).then(([v, c, m, team]) => {
    const people: RosterPerson[] = [];
    const seen = new Set<string>();
    const add = (name: any, kind: string) => { const n = String(name || '').trim(); const k = n.toLowerCase(); if (n && !seen.has(k)) { seen.add(k); people.push({ name: n, kind }); } };
    // the office + field team first (the people a WO usually goes to), then inspectors, then cleaners
    ((team && team.people) || []).filter((x: any) => x && x.role !== 'owner' && x.role !== 'accounting').forEach((x: any) => add(x.full_name || x.email, ROLE_LABEL[x.role] || 'Team'));
    ((m && (m.managers || m.inspectors)) || []).forEach((x: any) => add(x && x.name, 'Inspector'));
    ((c && c.cleaners) || []).filter((x: any) => x && x.active !== false).forEach((x: any) => add(x.name, 'Cleaner'));
    const vendors: RosterVendor[] = ((v && v.vendors) || [])
      .filter((x: any) => x && x.id != null && !x.archived && String(x.name || '').trim())
      .map((x: any) => ({ id: String(x.id), name: String(x.name).trim() }))
      .sort((a: RosterVendor, b: RosterVendor) => a.name.localeCompare(b.name));
    return { people, vendors };
  });
  rosterCache = p;
  // a failed (or partly failed) load is not cached: the next open asks again
  p.then(() => { if (partial && rosterCache === p) rosterCache = null; }, () => { if (rosterCache === p) rosterCache = null; });
  return p;
}
/** Test hook: forget the cached rosters. */
export function __resetDispatchRosters(): void { rosterCache = null; }

const KIND_GROUP = (kind: string): string => (kind === 'Cleaner' ? 'Cleaners' : kind === 'Inspector' ? 'Inspectors' : 'Team');
/** The Person <option>s, grouped Team / Inspectors / Cleaners; a current name not on the roster stays selectable. */
export function personOptionsHtml(people: RosterPerson[], current?: string | null): string {
  const cur = String(current || '').trim();
  const groups = new Map<string, string[]>();
  let found = false;
  for (const p of people || []) {
    if (!p || !p.name) continue;
    const sel = !!cur && p.name.toLowerCase() === cur.toLowerCase();
    if (sel) found = true;
    const g = KIND_GROUP(p.kind);
    if (!groups.has(g)) groups.set(g, []);
    const kindTxt = g === 'Team' && p.kind && p.kind !== 'Team' ? ` · ${esc(p.kind)}` : '';
    groups.get(g)!.push(`<option value="${esc(p.name)}"${sel ? ' selected' : ''}>${esc(p.name)}${kindTxt}</option>`);
  }
  const extra = cur && !found ? `<option value="${esc(cur)}" selected>${esc(cur)} (not on a roster)</option>` : '';
  return extra + ['Team', 'Inspectors', 'Cleaners'].filter(g => groups.has(g)).map(g => `<optgroup label="${g}">${groups.get(g)!.join('')}</optgroup>`).join('');
}
/** The Vendor <option>s by id; a current vendor that is archived / gone stays selectable by its id. */
export function vendorOptionsHtml(vendors: RosterVendor[], currentId?: string | null, currentName?: string | null): string {
  const cur = currentId == null ? '' : String(currentId);
  let found = false;
  const opts = (vendors || []).filter(v => v && v.id).map(v => {
    const sel = !!cur && v.id === cur;
    if (sel) found = true;
    return `<option value="${esc(v.id)}"${sel ? ' selected' : ''}>${esc(v.name)}</option>`;
  }).join('');
  const extra = cur && !found ? `<option value="${esc(cur)}" selected>${esc(currentName || 'Current vendor')} (not in the vendor list)</option>` : '';
  return extra + opts;
}

/** Person, Vendor, Day (+ Today / Tomorrow), Due (+ at next checkout). */
export function woDispatchFieldsHtml(o: { people: RosterPerson[]; vendors: RosterVendor[]; value?: WoDispatchValue }): string {
  ensureCss();
  const v = o.value || {};
  const checkout = v.due_source === 'checkout';
  const today = bizDay();
  const tomorrow = addDaysStr(today, 1) || '';
  const day = v.scheduled_date && DAY.test(v.scheduled_date) ? v.scheduled_date : '';
  const chip = (n: number, label: string, d: string) => `<button type="button" class="chip wof-chip${day && day === d ? ' active' : ''}" data-wof-day="${n}" aria-pressed="${day && day === d ? 'true' : 'false'}">${label}</button>`;
  return `<div class="wof" data-wof-root>
    <label class="wof-f"><span class="wof-l">Person</span><select class="wof-in" data-wof="assigned_to"><option value="">Nobody yet</option>${personOptionsHtml(o.people || [], v.assigned_to)}</select></label>
    <label class="wof-f"><span class="wof-l">Vendor</span><select class="wof-in" data-wof="vendor_id"><option value="">No vendor</option>${vendorOptionsHtml(o.vendors || [], v.vendor_id)}</select></label>
    <div class="wof-f"><span class="wof-l">Day</span><div class="wof-row"><input type="date" class="wof-in wof-date" data-wof="scheduled_date" value="${esc(day)}" aria-label="Day"><span class="wof-chips">${chip(0, 'Today', today)}${chip(1, 'Tomorrow', tomorrow)}</span></div></div>
    <div class="wof-f"><span class="wof-l">Due</span><div class="wof-row"><input type="date" class="wof-in wof-date" data-wof="due_date" value="${esc(checkout ? '' : (v.due_date && DAY.test(v.due_date) ? v.due_date : ''))}" aria-label="Due"${checkout ? ' disabled' : ''}><label class="wof-chk"><input type="checkbox" data-wof="due_checkout"${checkout ? ' checked' : ''}> at next checkout</label></div></div>
    <div class="wof-help">Pick a person, a vendor, or both. Days are Pacific time.</div>
  </div>`;
}

/**
 * Wires the day chips (Pacific today / tomorrow), the checkout toggle and the chip state.
 * Safe to call after every redraw. onChange (optional) hears every edit, chips included.
 */
export function bindWoDispatchFields(root: HTMLElement, opts: { onChange?: (v: WoDispatchValue) => void } = {}): void {
  const host = (root.querySelector('[data-wof-root]') as HTMLElement | null) || root;
  if (host.dataset.wofBound === '1') return;
  host.dataset.wofBound = '1';
  const day = host.querySelector('[data-wof="scheduled_date"]') as HTMLInputElement | null;
  const due = host.querySelector('[data-wof="due_date"]') as HTMLInputElement | null;
  const co = host.querySelector('[data-wof="due_checkout"]') as HTMLInputElement | null;
  const chips = Array.from(host.querySelectorAll<HTMLElement>('[data-wof-day]'));
  const syncChips = () => {
    const today = bizDay();
    for (const b of chips) {
      const on = !!day && !!day.value && day.value === addDaysStr(today, Number(b.dataset.wofDay) || 0);
      b.classList.toggle('active', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  };
  const changed = () => { if (opts.onChange) { try { opts.onChange(readWoDispatchFields(host)); } catch { /* host's problem */ } } };
  chips.forEach(b => b.addEventListener('click', () => {
    if (!day) return;
    day.value = addDaysStr(bizDay(), Number(b.dataset.wofDay) || 0) || '';
    day.dispatchEvent(new Event('input', { bubbles: true }));
    day.dispatchEvent(new Event('change', { bubbles: true }));
  }));
  day?.addEventListener('input', syncChips);
  day?.addEventListener('change', syncChips);
  co?.addEventListener('change', () => { if (due) { due.disabled = co.checked; if (co.checked) due.value = ''; } });
  host.querySelectorAll<HTMLElement>('[data-wof]').forEach(el => { el.addEventListener('change', changed); if (el.tagName === 'INPUT') el.addEventListener('input', changed); });
  syncChips();
}

/** Reads the fields back (only what is filled). */
export function readWoDispatchFields(root: HTMLElement): WoDispatchValue {
  const val = (k: string) => { const el = root.querySelector(`[data-wof="${k}"]`) as HTMLInputElement | HTMLSelectElement | null; return el ? String(el.value || '').trim() : ''; };
  const out: WoDispatchValue = {};
  const person = val('assigned_to'); if (person) out.assigned_to = person;
  const vendor = val('vendor_id'); if (vendor) out.vendor_id = vendor;
  const day = val('scheduled_date'); if (DAY.test(day)) out.scheduled_date = day;
  const co = root.querySelector('[data-wof="due_checkout"]') as HTMLInputElement | null;
  if (co && co.checked) out.due_source = 'checkout';
  else { const due = val('due_date'); if (DAY.test(due)) out.due_date = due; }
  return out;
}

// tokens only; ≥36px controls, 16px inputs on phones
let cssDone = false;
function ensureCss(): void {
  if (cssDone || typeof document === 'undefined') return;
  if (document.getElementById('wof-css')) { cssDone = true; return; }
  const st = document.createElement('style');
  st.id = 'wof-css';
  st.textContent = `
  .wof { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:12px 14px; }
  .wof-f { display:flex; flex-direction:column; gap:5px; min-width:0; margin:0; }
  .wof-l { font-size:10.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--text-muted); }
  .wof-row { display:flex; flex-wrap:wrap; align-items:center; gap:6px; min-width:0; }
  .wof-in { box-sizing:border-box; width:100%; min-width:0; max-width:100%; min-height:36px; padding:7px 10px; border:1px solid var(--border); border-radius:var(--radius-sm); font:inherit; font-size:13px; background:var(--card); color:var(--text); }
  .wof-in:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
  .wof-in:disabled { background:var(--bg-subtle); color:var(--text-faint); }
  .wof-date { flex:1 1 140px; width:auto; }
  .wof-chips { display:inline-flex; gap:6px; flex:none; }
  .wof-chip { min-height:36px; }
  .wof-chk { display:inline-flex; align-items:center; gap:6px; min-height:36px; font-size:12.5px; color:var(--text); cursor:pointer; white-space:nowrap; }
  .wof-chk input { width:16px; height:16px; margin:0; accent-color:var(--accent); }
  .wof-help { grid-column:1 / -1; font-size:11.5px; color:var(--text-muted); line-height:1.45; }
  @media (max-width: 640px) {
    .wof { grid-template-columns:minmax(0, 1fr); }
    .wof-in { font-size:16px; min-height:40px; }
    .wof-chip, .wof-chk { min-height:40px; }
  }
  `;
  document.head.appendChild(st);
  cssDone = true;
}
